/* eslint-disable react-hooks/exhaustive-deps */
import { motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRealtimeMatch } from '../hooks/useRealtimeMatch';

const allPlayers = [
  { id: 'red', name: 'Red Player', colorClass: 'red-token-ui' },
  { id: 'green', name: 'Green Player', colorClass: 'green-token-ui' },
  { id: 'yellow', name: 'Yellow Player', colorClass: 'yellow-token-ui' },
  { id: 'blue', name: 'Blue Player', colorClass: 'blue-token-ui' },
];

const commonPath = [
  { row: 6, col: 1 }, { row: 6, col: 2 }, { row: 6, col: 3 }, { row: 6, col: 4 }, { row: 6, col: 5 },
  { row: 5, col: 6 }, { row: 4, col: 6 }, { row: 3, col: 6 }, { row: 2, col: 6 }, { row: 1, col: 6 },
  { row: 0, col: 6 }, { row: 0, col: 7 }, { row: 0, col: 8 }, { row: 1, col: 8 }, { row: 2, col: 8 },
  { row: 3, col: 8 }, { row: 4, col: 8 }, { row: 5, col: 8 }, { row: 6, col: 9 }, { row: 6, col: 10 },
  { row: 6, col: 11 }, { row: 6, col: 12 }, { row: 6, col: 13 }, { row: 6, col: 14 }, { row: 7, col: 14 },
  { row: 8, col: 14 }, { row: 8, col: 13 }, { row: 8, col: 12 }, { row: 8, col: 11 }, { row: 8, col: 10 },
  { row: 8, col: 9 }, { row: 9, col: 8 }, { row: 10, col: 8 }, { row: 11, col: 8 }, { row: 12, col: 8 },
  { row: 13, col: 8 }, { row: 14, col: 8 }, { row: 14, col: 7 }, { row: 14, col: 6 }, { row: 13, col: 6 },
  { row: 12, col: 6 }, { row: 11, col: 6 }, { row: 10, col: 6 }, { row: 9, col: 6 }, { row: 8, col: 5 },
  { row: 8, col: 4 }, { row: 8, col: 3 }, { row: 8, col: 2 }, { row: 8, col: 1 }, { row: 8, col: 0 },
  { row: 7, col: 0 }, { row: 6, col: 0 },
];

const startIndexes = { red: 0, green: 13, yellow: 26, blue: 39 };
const safeCommonSteps = new Set([0, 8, 13, 21, 26, 34, 39, 47]);
const FINAL_STEP = 56;

const homeLanes = {
  red: [{ row: 7, col: 1 }, { row: 7, col: 2 }, { row: 7, col: 3 }, { row: 7, col: 4 }, { row: 7, col: 5 }],
  green: [{ row: 1, col: 7 }, { row: 2, col: 7 }, { row: 3, col: 7 }, { row: 4, col: 7 }, { row: 5, col: 7 }],
  yellow: [{ row: 7, col: 13 }, { row: 7, col: 12 }, { row: 7, col: 11 }, { row: 7, col: 10 }, { row: 7, col: 9 }],
  blue: [{ bottom: 0 }],
};

homeLanes.blue = [{ row: 13, col: 7 }, { row: 12, col: 7 }, { row: 11, col: 7 }, { row: 10, col: 7 }, { row: 9, col: 7 }];

const homePositions = {
  red: [{ top: '16%', left: '16%' }, { top: '16%', left: '26%' }, { top: '26%', left: '16%' }, { top: '26%', left: '26%' }],
  green: [{ top: '16%', right: '26%' }, { top: '16%', right: '16%' }, { top: '26%', right: '26%' }, { top: '26%', right: '16%' }],
  yellow: [{ bottom: '16%', right: '26%' }, { bottom: '16%', right: '16%' }, { bottom: '26%', right: '26%' }, { bottom: '26%', right: '16%' }],
  blue: [{ bottom: '16%', left: '16%' }, { bottom: '16%', left: '26%' }, { bottom: '26%', left: '16%' }, { bottom: '26%', left: '26%' }],
};

const centerOffsets = [{ top: '50%', left: '50%' }, { top: '47%', left: '53%' }, { top: '53%', left: '47%' }, { top: '47%', left: '47%' }];
const initialTokens = { red: [-1, -1, -1, -1], green: [-1, -1, -1, -1], yellow: [-1, -1, -1, -1], blue: [-1, -1, -1, -1] };
const LOCAL_COMPETITIVE_STATS_KEY = 'ludo-local-competitive-stats-v2';
const LOCAL_SEAT_KEYS = ['seat-1', 'seat-2', 'seat-3', 'seat-4'];

function shuffleArray(items = []) {
  const next = [...items];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }
  return next;
}

function getModeConfig(selectedMode) {
  return { order: shuffleArray(['red', 'green', 'yellow', 'blue']), bots: [] };
}

function isSafeStep(step) {
  return step >= 0 && step <= 50 && safeCommonSteps.has(step);
}

function getGlobalPathIndex(playerId, step) {
  return (startIndexes[playerId] + step) % commonPath.length;
}

function getTokenPoint(playerId, step, tokenIndex) {
  if (step === -1) return homePositions[playerId][tokenIndex];
  if (step === FINAL_STEP) return centerOffsets[tokenIndex];
  if (step <= 50) {
    const point = commonPath[getGlobalPathIndex(playerId, step)];
    return { top: `calc(${((point.row + 0.5) / 15) * 100}%)`, left: `calc(${((point.col + 0.5) / 15) * 100}%)` };
  }
  const lanePoint = homeLanes[playerId][step - 51];
  return { top: `calc(${((lanePoint.row + 0.5) / 15) * 100}%)`, left: `calc(${((lanePoint.col + 0.5) / 15) * 100}%)` };
}

function getProgressSummary(tokensForPlayer = []) {
  const opened = tokensForPlayer.filter((step) => step >= 0).length;
  const finished = tokensForPlayer.filter((step) => step === FINAL_STEP).length;
  return { opened, finished };
}

function normalizeCompetitiveStats(stats = {}) {
  return {
    totalWins: Math.max(0, Number(stats.totalWins || 0) || 0),
    totalLosses: Math.max(0, Number(stats.totalLosses || 0) || 0),
    consecutiveLosses: Math.max(0, Number(stats.consecutiveLosses || 0) || 0),
    updatedAt: stats.updatedAt || null,
  };
}

function getEmptyLocalCompetitiveStats() {
  return Object.fromEntries(LOCAL_SEAT_KEYS.map((seatKey) => [seatKey, normalizeCompetitiveStats()]));
}

function loadLocalCompetitiveStats() {
  if (typeof window === 'undefined') return getEmptyLocalCompetitiveStats();
  try {
    const raw = JSON.parse(window.localStorage.getItem(LOCAL_COMPETITIVE_STATS_KEY) || '{}');
    return Object.fromEntries(LOCAL_SEAT_KEYS.map((seatKey) => [seatKey, normalizeCompetitiveStats(raw?.[seatKey] || {})]));
  } catch {
    return getEmptyLocalCompetitiveStats();
  }
}


const SOUND_PREF_KEY = 'ludo-sound-enabled';

function loadSoundEnabled() {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(SOUND_PREF_KEY);
    return raw == null ? true : raw !== 'false';
  } catch {
    return true;
  }
}

function persistSoundEnabled(value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SOUND_PREF_KEY, String(Boolean(value)));
  } catch {}
}

function playTone(context, startAt, frequency = 440, duration = 0.12, gainValue = 0.03, type = 'sine') {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, startAt);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, gainValue), startAt + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(startAt);
  oscillator.stop(startAt + duration + 0.02);
}

function runSoundPattern(context, pattern = []) {
  if (!context || !pattern.length) return;
  const startAt = context.currentTime + 0.01;
  pattern.forEach((step, index) => {
    playTone(
      context,
      startAt + (step.delay ?? index * 0.08),
      step.frequency,
      step.duration,
      step.gain,
      step.type,
    );
  });
}

function vibratePattern(pattern = []) {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
  try {
    navigator.vibrate(pattern);
  } catch {}
}

function getAdaptiveHumanDiceFromStats(stats = {}) {
  const normalizedStats = normalizeCompetitiveStats(stats);
  const randomValue = Math.random();
  if (normalizedStats.consecutiveLosses === 2 || normalizedStats.consecutiveLosses === 3) {
    if (randomValue < 0.58) return 5 + Math.floor(Math.random() * 2);
    if (randomValue < 0.80) return 4;
    return 1 + Math.floor(Math.random() * 3);
  }
  if (normalizedStats.totalWins >= 1) {
    if (randomValue < 0.50) return 1 + Math.floor(Math.random() * 3);
    if (randomValue < 0.80) return 4;
    return 5 + Math.floor(Math.random() * 2);
  }
  return Math.floor(Math.random() * 6) + 1;
}

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickWeightedDice(values = []) {
  if (!values.length) return randomInt(1, 6);
  return values[Math.floor(Math.random() * values.length)];
}

function getBotFinishingNeeds(tokens = []) {
  return tokens
    .filter((step) => step >= 0 && step < FINAL_STEP)
    .map((step) => FINAL_STEP - step)
    .filter((need) => need >= 1 && need <= 6);
}

function getBotCaptureNeeds(playerId, snapshot = {}) {
  const ownTokens = snapshot[playerId] || [];
  const needs = [];
  ownTokens.forEach((step) => {
    if (step < 0 || step >= FINAL_STEP || isSafeStep(step)) return;
    const currentGlobal = getGlobalPathIndex(playerId, step);
    allPlayers.forEach((other) => {
      if (other.id === playerId) return;
      (snapshot[other.id] || []).forEach((otherStep) => {
        if (otherStep < 0 || otherStep >= FINAL_STEP || isSafeStep(otherStep)) return;
        const distance = (getGlobalPathIndex(other.id, otherStep) - currentGlobal + 52) % 52;
        if (distance >= 1 && distance <= 6) {
          needs.push(distance);
          if (!['red', 'green'].includes(other.id)) needs.push(distance);
        }
      });
    });
  });
  return needs;
}

function getBotFavouredDice(playerId, snapshot = {}, botCount = 1, humanCount = 0) {
  const basePool = [6, 6, 6, 6, 5, 5, 5, 4, 4, 4, 3, 3, 2, 1];
  const tokens = snapshot[playerId] || [];
  const unopenedCount = tokens.filter((step) => step === -1).length;
  const preferredPool = [];
  if (unopenedCount > 0) preferredPool.push(6, 6, 6, 6, 5, 4);
  getBotFinishingNeeds(tokens).forEach((need) => preferredPool.push(need, need, need, need));
  getBotCaptureNeeds(playerId, snapshot).forEach((need) => preferredPool.push(need, need, need));
  if (botCount === 1 && humanCount >= 3) preferredPool.push(6, 6, 6, 6, 5, 5, 4);
  const boostedPool = preferredPool.length && Math.random() < 0.72 ? preferredPool : basePool.concat(preferredPool);
  return pickWeightedDice(boostedPool);
}

export default function GameBoard({ onBack, onResultClose, selectedMode, currentMatch, profile, walletBalance = 0, walletStatus = 'Wallet synced.', onMatchComplete }) {
  const [localGameSeed, setLocalGameSeed] = useState(0);
  const modeConfig = useMemo(() => getModeConfig(selectedMode), [selectedMode, localGameSeed]);
  const localPlayers = useMemo(() => modeConfig.order.map((id, index) => ({ ...allPlayers.find((player) => player.id === id), statsKey: LOCAL_SEAT_KEYS[index] || `seat-${index + 1}` })), [modeConfig]);
  const botSet = useMemo(() => new Set(modeConfig.bots), [modeConfig]);
  const resultReportedRef = useRef(false);
  const botMoveMemoryRef = useRef({});
  const online = useRealtimeMatch(currentMatch, profile);

  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [dice, setDice] = useState(null);
  const [canMove, setCanMove] = useState(false);
  const [message, setMessage] = useState('Click Roll Dice to start.');
  const [tokens, setTokens] = useState(initialTokens);
  const [winners, setWinners] = useState([]);
  const [resultModal, setResultModal] = useState(null);
  const [refreshingResult, setRefreshingResult] = useState(false);
  const [localCompetitiveStats, setLocalCompetitiveStats] = useState(() => loadLocalCompetitiveStats());
  const [matchStartCompetitiveStats, setMatchStartCompetitiveStats] = useState(() => loadLocalCompetitiveStats());
  const localStatsCommittedRef = useRef(false);
  const [soundEnabled, setSoundEnabled] = useState(() => loadSoundEnabled());
  const audioContextRef = useRef(null);
  const soundUnlockedRef = useRef(false);
  const pressLockRef = useRef({});
  const previousOnlineSnapshotRef = useRef({ diceValue: null, phase: null, winnersLength: 0, message: '' });

  const boardCells = useMemo(() => Array.from({ length: 15 * 15 }, (_, index) => ({ index, row: Math.floor(index / 15), col: index % 15 })), []);
  const onlinePlayers = useMemo(() => {
    if (!online.gameState?.players?.length) return [];
    return online.gameState.players.map((player) => ({ ...player, colorClass: player.colorClass || `${player.id}-token-ui` }));
  }, [online.gameState]);

  const displayPlayers = online.isOnlineMatch ? onlinePlayers : localPlayers;
  const seatPlayers = useMemo(() => {
    if (!online.isOnlineMatch) return displayPlayers;
    const byId = new Map(onlinePlayers.map((player) => [player.id, player]));
    return ['red', 'green', 'blue', 'yellow']
      .map((playerId) => byId.get(playerId) || allPlayers.find((player) => player.id === playerId))
      .filter(Boolean);
  }, [online.isOnlineMatch, onlinePlayers, displayPlayers]);
  const displayTokens = online.isOnlineMatch ? (online.gameState?.tokens || initialTokens) : tokens;
  const displayCurrentPlayerIndex = online.isOnlineMatch ? (online.gameState?.currentPlayerIndex ?? 0) : currentPlayerIndex;
  const displayDice = online.isOnlineMatch ? (online.gameState?.dice ?? null) : dice;
  const displayCanMove = online.isOnlineMatch ? Boolean(online.gameState?.canMove) : canMove;
  const displayMessage = online.isOnlineMatch ? (online.gameState?.message || 'Waiting for server state...') : message;
  const displayPhase = online.isOnlineMatch ? (online.gameState?.phase || 'roll') : (displayCanMove ? 'move' : 'roll');
  const displayWinners = online.isOnlineMatch ? (online.gameState?.winners || []) : winners;
  const currentPlayer = displayPlayers[displayCurrentPlayerIndex];
  const [privateSecondsLeft, setPrivateSecondsLeft] = useState(0);
  const [uiNow, setUiNow] = useState(Date.now());
  const diceVisibleUntil = online.isOnlineMatch ? Number(online.gameState?.lastDiceVisibleUntil || 0) : 0;
  const diceOverlayVisible = online.isOnlineMatch && diceVisibleUntil > uiNow;
  const diceCountdown = diceOverlayVisible ? Math.max(0, Math.ceil((diceVisibleUntil - uiNow) / 1000)) : 0;
  const lastDicePlayer = online.isOnlineMatch ? displayPlayers.find((player) => player.id === online.gameState?.lastDiceBy) : null;
  const lastDicePlayerName = lastDicePlayer?.name || 'Player';
  const phaseLabelMap = { roll: 'Roll Dice', transition: 'Showing Dice', move: 'Move Token', finished: 'Match Finished' };
  const phaseLabel = phaseLabelMap[displayPhase] || 'Live';
  const resultSummary = online.isOnlineMatch ? (online.gameState?.resultSummary || null) : null;
  const isAllHumanLocalMatch = !online.isOnlineMatch && botSet.size === 0;
  const trustMessage = !online.isOnlineMatch
    ? 'Offline board running.'
    : (diceOverlayVisible
      ? `${lastDicePlayerName} rolled ${online.gameState?.lastDiceValue}.`
      : (displayPhase === 'move'
        ? `${currentPlayer?.name || 'Player'} can move now.`
        : (displayPhase === 'roll'
          ? `${currentPlayer?.name || 'Player'} can roll now.`
          : (displayPhase === 'finished' ? 'Match finished.' : 'Server is resolving the roll.'))));
  const selectablePlayerId = online.isOnlineMatch
    ? (online.isMyTurn && online.isMovePhase ? online.myPlayerId : null)
    : (displayCanMove ? currentPlayer?.id : null);
  const selectableMoves = useMemo(() => {
    if (online.isOnlineMatch) {
      return online.isMyTurn && online.isMovePhase && Array.isArray(online.gameState?.validMoves)
        ? online.gameState.validMoves
        : [];
    }
    if (!displayCanMove || !currentPlayer || displayDice == null) return [];
    return getMovableTokens(currentPlayer.id, displayDice, displayTokens);
  }, [online.isOnlineMatch, online.isMyTurn, online.isMovePhase, online.gameState?.validMoves, displayCanMove, currentPlayer, displayDice, displayTokens]);
  const selectableMoveSet = useMemo(() => new Set(selectableMoves), [selectableMoves]);
  const spotlightToneClass = currentPlayer?.colorClass || '';
  const topStatusMessage = online.isOnlineMatch
    ? (online.isMyTurn
      ? (displayPhase === 'move' ? 'Select a glowing token to complete your move.' : trustMessage)
      : trustMessage)
    : displayMessage;
  const actionButtonLabel = online.isOnlineMatch
    ? (online.gameState?.completed
      ? 'Match Finished'
      : (online.isMyTurn
        ? (online.isRollPhase ? 'Roll Dice' : (online.isTransitionPhase ? 'Showing Dice...' : 'Choose Token'))
        : (online.isTransitionPhase ? 'Showing Dice...' : 'Opponent Turn')))
    : 'Roll Dice';
  const actionButtonDisabled = online.isOnlineMatch
    ? (!online.isMyTurn || !online.isRollPhase || online.gameState?.completed)
    : (displayDice !== null || botSet.has(currentPlayer?.id));

  function ensureAudioUnlocked() {
    if (typeof window === 'undefined' || !soundEnabled) return null;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    if (!audioContextRef.current) audioContextRef.current = new AudioContextClass();
    const context = audioContextRef.current;
    if (context.state === 'suspended') context.resume().catch(() => {});
    soundUnlockedRef.current = true;
    return context;
  }

  function playUiSound(kind) {
    if (!soundEnabled) return;
    const context = ensureAudioUnlocked();
    if (!context) return;
    const patterns = {
      roll: [
        { frequency: 420, duration: 0.05, gain: 0.02, type: 'square' },
        { frequency: 520, duration: 0.06, gain: 0.026, type: 'square', delay: 0.06 },
        { frequency: 660, duration: 0.08, gain: 0.03, type: 'triangle', delay: 0.14 },
      ],
      move: [
        { frequency: 520, duration: 0.05, gain: 0.024, type: 'triangle' },
        { frequency: 660, duration: 0.08, gain: 0.028, type: 'triangle', delay: 0.06 },
      ],
      capture: [
        { frequency: 280, duration: 0.06, gain: 0.03, type: 'sawtooth' },
        { frequency: 180, duration: 0.09, gain: 0.032, type: 'sawtooth', delay: 0.05 },
      ],
      win: [
        { frequency: 523, duration: 0.08, gain: 0.022, type: 'triangle' },
        { frequency: 659, duration: 0.09, gain: 0.024, type: 'triangle', delay: 0.09 },
        { frequency: 784, duration: 0.16, gain: 0.03, type: 'triangle', delay: 0.19 },
      ],
      tap: [{ frequency: 500, duration: 0.04, gain: 0.016, type: 'square' }],
    };
    runSoundPattern(context, patterns[kind] || patterns.tap);
    if (kind === 'roll') vibratePattern([18]);
    if (kind === 'move') vibratePattern([10]);
    if (kind === 'capture') vibratePattern([18, 20, 18]);
    if (kind === 'win') vibratePattern([26, 34, 26]);
  }

  function toggleSound() {
    const nextValue = !soundEnabled;
    setSoundEnabled(nextValue);
    persistSoundEnabled(nextValue);
    if (nextValue) {
      ensureAudioUnlocked();
      setTimeout(() => playUiSound('tap'), 0);
    }
  }

  function triggerMoveFromTouch(tokenIndex, event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    ensureAudioUnlocked();
    moveToken(tokenIndex);
  }

  function rememberTouchStart(tokenIndex, event) {
    event?.preventDefault?.();
    ensureAudioUnlocked();
    pressLockRef.current[tokenIndex] = Date.now();
  }

  function maybeTriggerTouchEnd(tokenIndex, event) {
    const startedAt = pressLockRef.current[tokenIndex];
    delete pressLockRef.current[tokenIndex];
    if (!startedAt) return;
    triggerMoveFromTouch(tokenIndex, event);
  }

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const unlockAudio = () => {
      ensureAudioUnlocked();
    };
    window.addEventListener('pointerdown', unlockAudio, { passive: true });
    window.addEventListener('touchstart', unlockAudio, { passive: true });
    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
    };
  }, [soundEnabled]);

  useEffect(() => {
    if (!online.isOnlineMatch) return;
    const previous = previousOnlineSnapshotRef.current;
    const diceValue = online.gameState?.lastDiceValue ?? null;
    const phase = online.gameState?.phase || null;
    const winnersLength = Array.isArray(online.gameState?.winners) ? online.gameState.winners.length : 0;
    const messageText = String(online.gameState?.message || '').toLowerCase();

    if (diceValue != null && diceValue !== previous.diceValue) playUiSound('roll');
    if (phase === 'move' && previous.phase !== 'move' && online.isMyTurn) playUiSound('move');
    if ((messageText.includes('captured') || messageText.includes('capture')) && messageText !== previous.message) playUiSound('capture');
    if (winnersLength > previous.winnersLength) playUiSound('win');

    previousOnlineSnapshotRef.current = { diceValue, phase, winnersLength, message: messageText };
  }, [online.isOnlineMatch, online.gameState?.lastDiceValue, online.gameState?.phase, online.gameState?.message, online.gameState?.winners, online.isMyTurn]);

  useEffect(() => {
    if (online.isOnlineMatch) return;
    setCurrentPlayerIndex(0);
    setDice(null);
    setCanMove(false);
    setTokens(initialTokens);
    setWinners([]);
    setResultModal(null);
    setRefreshingResult(false);
    resultReportedRef.current = false;
    localStatsCommittedRef.current = false;
    setMatchStartCompetitiveStats(loadLocalCompetitiveStats());
    setMessage(`Mode ready: ${selectedMode?.title || 'Classic'}. ${localPlayers[0]?.name || 'Red Player'} starts.`);
  }, [selectedMode, localPlayers, online.isOnlineMatch]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const unlockAudio = () => {
      ensureAudioUnlocked();
    };
    window.addEventListener('pointerdown', unlockAudio, { passive: true });
    window.addEventListener('touchstart', unlockAudio, { passive: true });
    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
    };
  }, [soundEnabled]);

  useEffect(() => {
    if (!online.isOnlineMatch) return;
    const previous = previousOnlineSnapshotRef.current;
    const diceValue = online.gameState?.lastDiceValue ?? null;
    const phase = online.gameState?.phase || null;
    const winnersLength = Array.isArray(online.gameState?.winners) ? online.gameState.winners.length : 0;
    const messageText = String(online.gameState?.message || '').toLowerCase();

    if (diceValue != null && diceValue !== previous.diceValue) playUiSound('roll');
    if (phase === 'move' && previous.phase !== 'move' && online.isMyTurn) playUiSound('move');
    if ((messageText.includes('captured') || messageText.includes('capture')) && messageText !== previous.message) playUiSound('capture');
    if (winnersLength > previous.winnersLength) playUiSound('win');

    previousOnlineSnapshotRef.current = { diceValue, phase, winnersLength, message: messageText };
  }, [online.isOnlineMatch, online.gameState?.lastDiceValue, online.gameState?.phase, online.gameState?.message, online.gameState?.winners, online.isMyTurn]);

  useEffect(() => {
    if (online.isOnlineMatch) return;
    if (!currentPlayer || !botSet.has(currentPlayer.id) || winners.includes(currentPlayer.id)) return;
    if (dice !== null || canMove) return;

    const timer = setTimeout(() => {
      const botDice = getLocalDiceValue(currentPlayer.id);
      setDice(botDice);
      const possibleMoves = getMovableTokens(currentPlayer.id, botDice, tokens);
      if (!possibleMoves.length) {
        const nextIndex = getNextEligiblePlayerIndex(currentPlayerIndex, localPlayers, winners);
        setCanMove(false);
        setMessage(`${currentPlayer.name} rolled ${botDice}, but no valid move. ${localPlayers[nextIndex].name}'s turn.`);
        setTimeout(() => {
          setDice(null);
          setCurrentPlayerIndex(nextIndex);
        }, 550);
        return;
      }
      const bestMove = pickBotMove(currentPlayer.id, botDice, tokens, possibleMoves);
      setCanMove(true);
      setMessage(`${currentPlayer.name} rolled ${botDice}. Bot is thinking...`);
      setTimeout(() => moveToken(bestMove, botDice), 450);
    }, 800);

    return () => clearTimeout(timer);
  }, [online.isOnlineMatch, currentPlayer, currentPlayerIndex, localPlayers, botSet, dice, canMove, tokens, winners]);

  useEffect(() => {
    if (!online.isOnlineMatch || !online.gameState?.turnEndsAt || !online.isMyTurn) {
      setPrivateSecondsLeft(0);
      return undefined;
    }
    const tick = () => {
      const secs = Math.max(0, Math.ceil((new Date(online.gameState.turnEndsAt).getTime() - Date.now()) / 1000));
      setPrivateSecondsLeft(secs);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [online.isOnlineMatch, online.gameState?.turnEndsAt, online.isMyTurn]);

  useEffect(() => {
    if (!online.isOnlineMatch || !diceVisibleUntil || diceVisibleUntil <= Date.now()) {
      setUiNow(Date.now());
      return undefined;
    }
    setUiNow(Date.now());
    const id = setInterval(() => setUiNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [online.isOnlineMatch, diceVisibleUntil]);

  useEffect(() => {
    if (!online.isOnlineMatch || !currentMatch?.roomId) return undefined;
    if (online.gameState?.completed) {
      localStorage.removeItem('ludo-last-active-match-id');
      localStorage.setItem('ludo-last-match-status', 'completed');
      return undefined;
    }
    localStorage.setItem('ludo-last-active-match-id', currentMatch.roomId);
    localStorage.setItem('ludo-last-match-status', online.gameState?.status || 'active');
    return undefined;
  }, [online.isOnlineMatch, online.gameState?.completed, online.gameState?.status, currentMatch?.roomId]);

  useEffect(() => {
    if (!online.isOnlineMatch || !online.gameState?.completed || !profile?.mobile) return undefined;
    if (resultReportedRef.current) return undefined;
    resultReportedRef.current = true;
    let cancelled = false;

    async function finalizeMatchResult() {
      setRefreshingResult(true);
      let walletData = null;
      if (onMatchComplete) {
        try {
          walletData = await onMatchComplete();
        } catch {}
      }
      if (cancelled) return;
      const isWinner = online.gameState?.winnerMobile === profile.mobile;
      const prizeAmount = Number(resultSummary?.prizeAmount ?? online.gameState?.prize ?? selectedMode?.prize ?? 0);
      const entryFee = Number(resultSummary?.entryFee ?? online.gameState?.entryFee ?? selectedMode?.fee ?? 0);
      const resolvedWalletBalance = Number(walletData?.balance ?? walletBalance ?? 0);
      setResultModal({
        isWinner,
        title: isWinner ? 'You Won' : 'You Lost',
        subtitle: isWinner ? 'First finisher rule applied. Match closed instantly.' : 'The first finisher closed the table. Better luck next time.',
        prizeAmount,
        entryFee,
        netAmount: isWinner ? prizeAmount - entryFee : -entryFee,
        walletBalance: resolvedWalletBalance,
        winnerName: displayPlayers.find((player) => player.id === online.gameState?.winner)?.name || 'Winner',
      });
      setRefreshingResult(false);
    }

    finalizeMatchResult();
    return () => {
      cancelled = true;
    };
  }, [online.isOnlineMatch, online.gameState?.completed, online.gameState?.winnerMobile, online.gameState?.winner, online.gameState?.prize, online.gameState?.entryFee, profile?.mobile, onMatchComplete, walletBalance, resultSummary, selectedMode?.prize, selectedMode?.fee, displayPlayers]);

  function getCellClass(row, col) {
    if (row < 6 && col < 6) return 'cell-red-zone';
    if (row < 6 && col > 8) return 'cell-green-zone';
    if (row > 8 && col < 6) return 'cell-blue-zone';
    if (row > 8 && col > 8) return 'cell-yellow-zone';
    if (row >= 6 && row <= 8 && col >= 6 && col <= 8) return 'cell-center';
    if (commonPath.some((point) => point.row === row && point.col === col)) {
      const commonIndex = commonPath.findIndex((point) => point.row === row && point.col === col);
      return safeCommonSteps.has(commonIndex) ? 'cell-safe' : 'cell-path';
    }
    if (Object.values(homeLanes).some((lane) => lane.some((point) => point.row === row && point.col === col))) return 'cell-home-lane';
    return 'cell-normal';
  }

  function getNextEligiblePlayerIndex(fromIndex, playerOrder = localPlayers, winnersList = winners) {
    for (let step = 1; step <= playerOrder.length; step += 1) {
      const nextIndex = (fromIndex + step) % playerOrder.length;
      if (!winnersList.includes(playerOrder[nextIndex].id)) return nextIndex;
    }
    return fromIndex;
  }

  function getMovableTokens(playerId, rolledDice, snapshot = tokens) {
    return snapshot[playerId]
      .map((step, tokenIndex) => ({ step, tokenIndex }))
      .filter(({ step }) => {
        if (step === FINAL_STEP) return false;
        if (step === -1) return rolledDice === 6;
        return step + rolledDice <= FINAL_STEP;
      })
      .map(({ tokenIndex }) => tokenIndex);
  }

  function getCaptureCandidates(playerId, targetStep, snapshot) {
    if (targetStep < 0 || targetStep > 50 || isSafeStep(targetStep)) return [];
    const targetGlobalIndex = getGlobalPathIndex(playerId, targetStep);
    const candidates = [];
    localPlayers.forEach((player) => {
      if (player.id === playerId) return;
      snapshot[player.id].forEach((step, tokenIndex) => {
        if (step < 0 || step > 50) return;
        if (getGlobalPathIndex(player.id, step) === targetGlobalIndex) candidates.push({ playerId: player.id, tokenIndex });
      });
    });
    return candidates;
  }

  function getThreatSummary(playerId, targetStep, snapshot) {
    const summary = { total: 0, human: 0, bot: 0 };
    if (targetStep < 0 || targetStep > 50 || isSafeStep(targetStep)) return summary;
    const targetGlobalIndex = getGlobalPathIndex(playerId, targetStep);
    localPlayers.forEach((player) => {
      if (player.id === playerId) return;
      const isHuman = !botSet.has(player.id);
      (snapshot[player.id] || []).forEach((step) => {
        if (step === FINAL_STEP || step > 50) return;
        if (step === -1) {
          if (targetGlobalIndex === startIndexes[player.id]) {
            summary.total += 1;
            if (isHuman) summary.human += 1;
            else summary.bot += 1;
          }
          return;
        }
        const distance = (targetGlobalIndex - getGlobalPathIndex(player.id, step) + commonPath.length) % commonPath.length;
        if (distance >= 1 && distance <= 6) {
          summary.total += 1;
          if (isHuman) summary.human += 1;
          else summary.bot += 1;
        }
      });
    });
    return summary;
  }

  function getPressureSummary(playerId, targetStep, snapshot) {
    const summary = { total: 0, human: 0, bot: 0 };
    if (targetStep < 0 || targetStep > 50) return summary;
    const targetGlobalIndex = getGlobalPathIndex(playerId, targetStep);
    localPlayers.forEach((player) => {
      if (player.id === playerId) return;
      const isHuman = !botSet.has(player.id);
      (snapshot[player.id] || []).forEach((step) => {
        if (step < 0 || step > 50 || isSafeStep(step)) return;
        const distance = (getGlobalPathIndex(player.id, step) - targetGlobalIndex + commonPath.length) % commonPath.length;
        if (distance >= 1 && distance <= 6) {
          summary.total += 1;
          if (isHuman) summary.human += 1;
          else summary.bot += 1;
        }
      });
    });
    return summary;
  }

  function saveLocalCompetitiveStats(nextStats) {
    setLocalCompetitiveStats(nextStats);
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(LOCAL_COMPETITIVE_STATS_KEY, JSON.stringify(nextStats));
    } catch {}
  }

  function commitLocalMatchOutcome(firstWinnerPlayerId) {
    if (!isAllHumanLocalMatch || localStatsCommittedRef.current || !firstWinnerPlayerId) return;
    const nextStats = { ...localCompetitiveStats };
    localPlayers.forEach((player) => {
      const statsKey = player.statsKey || player.id;
      const previousStats = normalizeCompetitiveStats(nextStats[statsKey] || {});
      nextStats[statsKey] = player.id === firstWinnerPlayerId
        ? {
            ...previousStats,
            totalWins: previousStats.totalWins + 1,
            consecutiveLosses: 0,
            updatedAt: new Date().toISOString(),
          }
        : {
            ...previousStats,
            totalLosses: previousStats.totalLosses + 1,
            consecutiveLosses: previousStats.consecutiveLosses + 1,
            updatedAt: new Date().toISOString(),
          };
    });
    localStatsCommittedRef.current = true;
    saveLocalCompetitiveStats(nextStats);
  }

  function getLocalDiceValue(playerId) {
    if (botSet.has(playerId)) return getBotFavouredDice(playerId, tokens, botSet.size, localPlayers.length - botSet.size);
    if (!isAllHumanLocalMatch) return Math.floor(Math.random() * 6) + 1;
    const localPlayer = localPlayers.find((player) => player.id === playerId);
    const statsKey = localPlayer?.statsKey || playerId;
    return getAdaptiveHumanDiceFromStats(matchStartCompetitiveStats[statsKey] || {});
  }

  function rollDice() {
    ensureAudioUnlocked();
    if (online.isOnlineMatch) {
      playUiSound('roll');
      online.rollDice();
      return;
    }
    if (dice !== null || !currentPlayer || botSet.has(currentPlayer.id)) return;
    const rolled = getLocalDiceValue(currentPlayer.id);
    playUiSound('roll');
    const validMoves = getMovableTokens(currentPlayer.id, rolled, tokens);
    setDice(rolled);
    if (!validMoves.length) {
      const nextIndex = getNextEligiblePlayerIndex(currentPlayerIndex);
      setCanMove(false);
      setMessage(`${currentPlayer.name} rolled ${rolled}, but no valid move. ${localPlayers[nextIndex].name}'s turn.`);
      setTimeout(() => {
        setDice(null);
        setCurrentPlayerIndex(nextIndex);
      }, 650);
      return;
    }
    setCanMove(true);
    setMessage(validMoves.length === 1 ? `${currentPlayer.name} rolled ${rolled}. Only Token ${validMoves[0] + 1} can move.` : `${currentPlayer.name} rolled ${rolled}. Choose a token.`);
    if (validMoves.length === 1) setTimeout(() => moveToken(validMoves[0], rolled), 280);
  }

  function completeTurn(extraTurnMessage, winnersList = winners) {
    const nextIndex = getNextEligiblePlayerIndex(currentPlayerIndex, localPlayers, winnersList);
    setDice(null);
    setCanMove(false);
    setCurrentPlayerIndex(nextIndex);
    setMessage(extraTurnMessage || `${localPlayers[nextIndex].name}'s turn.`);
  }

  function keepTurn(nextMessage) {
    setDice(null);
    setCanMove(false);
    setMessage(nextMessage || `${currentPlayer.name} gets another roll.`);
  }

  function maybeReportResult(winnerPlayerId) {
    if (!currentMatch?.roomId || !onMatchComplete || resultReportedRef.current) return;
    if (winnerPlayerId !== 'red') return;
    resultReportedRef.current = true;
    onMatchComplete({ roomId: currentMatch.roomId, winnerMobile: profile?.mobile, rewardCoins: selectedMode?.prize || 0 });
  }

  function moveToken(tokenIndex, forcedDice = dice) {
    if (online.isOnlineMatch) {
      online.moveToken(tokenIndex);
      return;
    }
    if (!currentPlayer || forcedDice === null) return;
    if (botSet.has(currentPlayer.id)) {
      const existing = botMoveMemoryRef.current[currentPlayer.id] || { lastTokenIndex: null, repeatCount: 0 };
      botMoveMemoryRef.current[currentPlayer.id] = {
        lastTokenIndex: tokenIndex,
        repeatCount: existing.lastTokenIndex === tokenIndex ? existing.repeatCount + 1 : 0,
      };
    }
    if (!canMove && !botSet.has(currentPlayer.id)) return;
    const playerId = currentPlayer.id;
    const validMoves = getMovableTokens(playerId, forcedDice, tokens);
    if (!validMoves.includes(tokenIndex)) return;

    const playerTokens = [...tokens[playerId]];
    const currentStep = playerTokens[tokenIndex];
    const nextStep = currentStep === -1 ? 0 : currentStep + forcedDice;
    const nextTokens = { ...tokens, [playerId]: playerTokens };
    playerTokens[tokenIndex] = nextStep;

    const captureCandidates = getCaptureCandidates(playerId, nextStep, nextTokens);
    const canCaptureNow = captureCandidates.length === 1;
    if (canCaptureNow) {
      const captured = captureCandidates[0];
      const capturedTokens = [...nextTokens[captured.playerId]];
      capturedTokens[captured.tokenIndex] = -1;
      nextTokens[captured.playerId] = capturedTokens;
    }

    const allFinished = nextTokens[playerId].every((step) => step === FINAL_STEP);
    const updatedWinners = allFinished && !winners.includes(playerId) ? [...winners, playerId] : winners;
    setTokens(nextTokens);
    if (updatedWinners !== winners) setWinners(updatedWinners);

    if (canCaptureNow) playUiSound('capture');
    else playUiSound('move');

    if (allFinished) {
      const place = updatedWinners.length;
      if (place === 1) commitLocalMatchOutcome(playerId);
      playUiSound('win');
      maybeReportResult(playerId);
      if (updatedWinners.length === localPlayers.length - 1) {
        setMessage(`${currentPlayer.name} finished in place ${place}. Match completed.`);
        setDice(null);
        setCanMove(false);
        return;
      }
      const nextIndex = getNextEligiblePlayerIndex(currentPlayerIndex, localPlayers, updatedWinners);
      setDice(null);
      setCanMove(false);
      setCurrentPlayerIndex(nextIndex);
      setMessage(`${currentPlayer.name} finished all tokens and secured place ${place}. ${localPlayers[nextIndex].name}'s turn.`);
      return;
    }

    if (nextStep === FINAL_STEP) return keepTurn(`${currentPlayer.name} finished Token ${tokenIndex + 1}. Bonus roll.`);
    if (canCaptureNow) return keepTurn(`${currentPlayer.name} captured an opponent token with Token ${tokenIndex + 1}. Bonus roll.`);
    if (forcedDice === 6) return keepTurn(`${currentPlayer.name} rolled 6 and moved Token ${tokenIndex + 1}. Roll again.`);

    const nextIndex = getNextEligiblePlayerIndex(currentPlayerIndex, localPlayers, updatedWinners);
    completeTurn(`${currentPlayer.name} moved Token ${tokenIndex + 1}. ${localPlayers[nextIndex].name}'s turn.`, updatedWinners);
  }

  function resetGame() {
    if (online.isOnlineMatch) {
      onBack();
      return;
    }
    setResultModal(null);
    setRefreshingResult(false);
    setLocalGameSeed((seed) => seed + 1);
  }

  function handleResultContinue() {
    setResultModal(null);
    localStorage.removeItem('ludo-current-match');
    localStorage.removeItem('ludo-last-active-match-id');
    localStorage.setItem('ludo-last-match-status', 'completed');
    if (onResultClose) {
      onResultClose();
      return;
    }
    onBack();
  }

  function pickBotMove(playerId, rolledDice, snapshot, possibleMoves) {
    const playerTokens = snapshot[playerId] || [];
    const unopenedCount = playerTokens.filter((step) => step === -1).length;
    const activeSteps = playerTokens.filter((step) => step >= 0 && step < FINAL_STEP);
    const minActiveStep = activeSteps.length ? Math.min(...activeSteps) : null;
    const maxActiveStep = activeSteps.length ? Math.max(...activeSteps) : null;
    const memory = botMoveMemoryRef.current[playerId] || { lastTokenIndex: null, repeatCount: 0 };

    let bestMove = possibleMoves[0];
    let bestScore = Number.NEGATIVE_INFINITY;

    possibleMoves.forEach((tokenIndex, choiceIndex) => {
      const currentStep = playerTokens[tokenIndex];
      const nextStep = currentStep === -1 ? 0 : currentStep + rolledDice;
      const isOpenMove = currentStep === -1;
      const isFinishMove = nextStep === FINAL_STEP;
      const finishDistance = nextStep >= 0 && nextStep < FINAL_STEP ? FINAL_STEP - nextStep : null;
      const currentThreat = currentStep >= 0 ? getThreatSummary(playerId, currentStep, snapshot) : { total: 0, human: 0, bot: 0 };

      const nextPlayerTokens = [...playerTokens];
      nextPlayerTokens[tokenIndex] = nextStep;
      const nextTokens = { ...snapshot, [playerId]: nextPlayerTokens };

      const captureCandidates = getCaptureCandidates(playerId, nextStep, nextTokens);
      const threatSummary = getThreatSummary(playerId, nextStep, nextTokens);
      const pressureSummary = getPressureSummary(playerId, nextStep, nextTokens);
      const sameSquareAllies = nextPlayerTokens.filter((step, idx) => idx !== tokenIndex && step === nextStep).length;
      const steppedOutOfSafe = currentStep >= 0 && isSafeStep(currentStep) && !isSafeStep(nextStep);
      const escapedThreat = Math.max(0, currentThreat.total - threatSummary.total);
      const entersHomeLane = currentStep < 51 && nextStep >= 51;
      const noActiveTokens = activeSteps.length === 0;

      let score = choiceIndex / 1000;

      if (isFinishMove) score += 500000;

      if (captureCandidates.length) {
        captureCandidates.forEach((captured) => {
          const capturedStep = snapshot[captured.playerId]?.[captured.tokenIndex] ?? -1;
          const isHumanTarget = !botSet.has(captured.playerId);
          score += isHumanTarget ? 250000 : 95000;
          score += Math.max(0, capturedStep) * (isHumanTarget ? 45 : 18);
        });
        if (threatSummary.total === 0) score += 18000;
      }

      if (isOpenMove) {
        score += noActiveTokens ? 52000 : 18000;
        if (rolledDice === 6) score += 22000;
        if (unopenedCount > 1) score += 5000;
        if (threatSummary.total > 0 && captureCandidates.length === 0) score -= 14000;
      } else {
        if (rolledDice === 6 && unopenedCount > 0 && captureCandidates.length === 0 && !isFinishMove) score -= 8000;
        if (minActiveStep !== null && currentStep === minActiveStep) score += 6500;
        if (maxActiveStep !== null && currentStep === maxActiveStep && unopenedCount > 0 && !isFinishMove) score -= 3000;
      }

      if (entersHomeLane) score += 32000;
      if (nextStep >= 51) score += 26000 + (nextStep * 180);
      if (finishDistance !== null && finishDistance <= 6) score += 42000 - (finishDistance * 4000);
      if (currentStep >= 45) score += 14000;
      if (isSafeStep(nextStep)) score += 22000;
      if (nextStep >= 0 && nextStep <= 50) score += nextStep * 34;

      score += escapedThreat * 15000;
      score += (pressureSummary.human * 15000) + (pressureSummary.bot * 1200);

      if (!isSafeStep(nextStep)) {
        score -= (threatSummary.human * 15000) + (threatSummary.bot * 2600);
      }
      if (steppedOutOfSafe && captureCandidates.length === 0 && !isFinishMove) {
        score -= 18000;
      }

      if (sameSquareAllies > 0) {
        score += isSafeStep(nextStep) ? 5000 : 2000;
        if (!isSafeStep(nextStep) && threatSummary.total > 0) score -= 4000;
      }

      if (rolledDice === 6 && isOpenMove && captureCandidates.length === 0 && threatSummary.total === 0) score += 8000;

      if (memory.lastTokenIndex === tokenIndex) {
        score -= 20000 + (memory.repeatCount * 6500);
      } else {
        score += 4000;
      }

      if (score > bestScore) {
        bestScore = score;
        bestMove = tokenIndex;
      }
    });

    return bestMove;
  }

  const topSeats = seatPlayers.slice(0, 2);
  const bottomSeats = seatPlayers.slice(2, 4);

  return (
    <motion.div className="game-screen portrait-reference-screen" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
      <div className="portrait-game-shell">
        <div className="portrait-topbar">
          <button className="portrait-icon-btn" onClick={onBack} type="button" aria-label="Back">‹</button>
          <div className="portrait-topbar-center">
            <span className="tiny-label">HTML5 Browser Ludo</span>
            <strong>{currentMatch?.roomId ? `Room ${currentMatch.roomId}` : 'Practice Table'}</strong>
          </div>
          <button className={`portrait-icon-btn ${soundEnabled ? 'sound-on' : 'sound-off'}`} onClick={toggleSound} type="button" aria-label={soundEnabled ? 'Mute sound' : 'Enable sound'}>{soundEnabled ? '🔊' : '🔇'}</button>
        </div>

        <div className={`turn-spotlight portrait-turn-spotlight ${spotlightToneClass} ${online.isOnlineMatch && online.isMyTurn ? 'my-turn-spotlight' : ''}`}>
          <div>
            <div className="tiny-label">Turn Spotlight</div>
            <h3>{currentPlayer?.name || '-'} • {phaseLabel}</h3>
            <p>{topStatusMessage}</p>
          </div>
          <div className="turn-spotlight-side">
            <span className={`board-status-pill ${online.isOnlineMatch && online.isMyTurn ? 'active' : ''}`}>{online.isOnlineMatch ? (online.isMyTurn ? 'Your Turn' : 'Watching Live') : 'Offline Match'}</span>
            <strong>{online.isOnlineMatch && online.isMyTurn ? `${privateSecondsLeft}s` : (diceOverlayVisible ? `${diceCountdown}s` : 'Live')}</strong>
          </div>
        </div>

      {resultModal && (
        <div className="match-result-overlay">
          <motion.div
            className={`match-result-modal ${resultModal.isWinner ? 'winner' : 'loser'}`}
            initial={{ opacity: 0, scale: 0.9, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.28 }}
          >
            <div className="result-top-strip">
              <span className="soft-chip">Wallet Synced</span>
              <span className="soft-chip">{resultModal.isWinner ? 'Prize Settled' : 'Result Locked'}</span>
            </div>
            <div className="result-emoji">{resultModal.isWinner ? '🏆' : '🎯'}</div>
            <div className="tiny-label">Match Result</div>
            <h2>{resultModal.title}</h2>
            <p>{resultModal.subtitle}</p>
            <div className="result-stat-grid">
              <div className="result-stat">
                <span>Prize Amount</span>
                <strong>+{resultModal.prizeAmount} Coins</strong>
              </div>
              <div className="result-stat">
                <span>Entry Fee</span>
                <strong>{resultModal.entryFee > 0 ? `-${resultModal.entryFee}` : 0} Coins</strong>
              </div>
              <div className="result-stat">
                <span>Net Result</span>
                <strong className={resultModal.netAmount >= 0 ? 'result-positive' : 'result-negative'}>{resultModal.netAmount >= 0 ? '+' : ''}{resultModal.netAmount} Coins</strong>
              </div>
              <div className="result-stat">
                <span>Updated Wallet</span>
                <strong>{resultModal.walletBalance} Coins</strong>
              </div>
            </div>
            {!resultModal.isWinner && <div className="soft-chip">Winner: {resultModal.winnerName}</div>}
            <div className="button-stack top-gap">
              <button className="primary-btn" onClick={handleResultContinue} type="button">Back to Lobby</button>
            </div>
          </motion.div>
        </div>
      )}

      <div className="game-layout portrait-reference-layout">
        <div className="board-centered-column">
          <div className="portrait-seat-row top">
            {topSeats.map((player) => {
              const progress = getProgressSummary(displayTokens[player.id] || []);
              const isWinner = displayWinners.includes(player.id);
              const isMyOnlinePlayer = online.isOnlineMatch && online.myPlayerId === player.id;
              const nameLabel = `${player.name}${isMyOnlinePlayer ? ' • You' : ''}`;
              return (
                <div key={player.id} className={`portrait-seat ${player.colorClass} ${currentPlayer?.id === player.id ? 'active-player-card' : ''}`}>
                  <div className="portrait-avatar">{nameLabel.slice(0,1)}</div>
                  <div className="portrait-seat-meta">
                    <strong>{nameLabel}</strong>
                    <span>{isWinner ? `Won #${displayWinners.indexOf(player.id) + 1}` : currentPlayer?.id === player.id ? 'Playing now' : `Open ${progress.opened} · Home ${progress.finished}`}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="portrait-board-column">
          <div className="real-board-wrap html5-board-shell portrait-board-shell">
            <div className="portrait-board-frame">
              <div className="portrait-wood-corner tl"></div>
              <div className="portrait-wood-corner tr"></div>
              <div className="portrait-wood-corner bl"></div>
              <div className="portrait-wood-corner br"></div>
              <div className="real-board portrait-real-board">
                {diceOverlayVisible && (
                  <div className="dice-result-overlay">
                    <div className="dice-result-badge">
                      <div className="tiny-label">Dice Result • {diceCountdown}s</div>
                      <div className="dice-result-number">{online.gameState?.lastDiceValue}</div>
                      <strong>{lastDicePlayerName} rolled {online.gameState?.lastDiceValue}</strong>
                      <span>Visible to all players before the turn resolves.</span>
                    </div>
                  </div>
                )}
                {boardCells.map((cell) => <div key={cell.index} className={`board-cell ${getCellClass(cell.row, cell.col)}`} />)}
                <div className="board-center-star portrait-board-center-star">★</div>
                {displayPlayers.flatMap((player) =>
                  (displayTokens[player.id] || []).map((step, tokenIndex) => {
                    const point = getTokenPoint(player.id, step, tokenIndex);
                    const isSelectableToken = player.id === selectablePlayerId && selectableMoveSet.has(tokenIndex);
                    const tokenClasses = `board-token portrait-token ${player.colorClass} ${step === FINAL_STEP ? 'finished-token' : ''} ${currentPlayer?.id === player.id ? 'active-turn-token' : ''} ${isSelectableToken ? 'clickable-token selectable-token' : ''}`;
                    if (isSelectableToken) {
                      return (
                        <button
                          key={`${player.id}-${tokenIndex}`}
                          className={tokenClasses}
                          style={point}
                          type="button"
                          onClick={() => moveToken(tokenIndex)}
                          onTouchStart={(event) => rememberTouchStart(tokenIndex, event)}
                          onTouchEnd={(event) => maybeTriggerTouchEnd(tokenIndex, event)}
                          title={`Move ${player.name} token ${tokenIndex + 1}`}
                        >
                          {tokenIndex + 1}
                        </button>
                      );
                    }
                    return <div key={`${player.id}-${tokenIndex}`} className={tokenClasses} style={point}>{tokenIndex + 1}</div>;
                  })
                )}
              </div>
            </div>
          </div>

          <div className="portrait-seat-row bottom">
            {bottomSeats.map((player) => {
              const progress = getProgressSummary(displayTokens[player.id] || []);
              const isWinner = displayWinners.includes(player.id);
              const isMyOnlinePlayer = online.isOnlineMatch && online.myPlayerId === player.id;
              const nameLabel = `${player.name}${isMyOnlinePlayer ? ' • You' : ''}`;
              return (
                <div key={player.id} className={`portrait-seat ${player.colorClass} ${currentPlayer?.id === player.id ? 'active-player-card' : ''}`}>
                  <div className="portrait-avatar">{nameLabel.slice(0,1)}</div>
                  <div className="portrait-seat-meta">
                    <strong>{nameLabel}</strong>
                    <span>{isWinner ? `Won #${displayWinners.indexOf(player.id) + 1}` : currentPlayer?.id === player.id ? 'Playing now' : `Open ${progress.opened} · Home ${progress.finished}`}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="portrait-action-dock">
            <div className="portrait-dice-zone">
              <div className={`dice-face dice-${online.isOnlineMatch ? (online.gameState?.lastDiceValue ?? displayDice ?? 1) : (displayDice ?? 1)} ${online.isOnlineMatch && online.isMyTurn && !actionButtonDisabled ? 'dice-face-active' : ''} ${diceOverlayVisible ? 'dice-face-result' : ''}`}>
                {Array.from({ length: 9 }, (_, dotIndex) => <span key={dotIndex} className="pip"></span>)}
              </div>
            </div>
            <div className="portrait-dock-copy">
              <div className="tiny-label">Play Guidance</div>
              <strong>{actionButtonLabel}</strong>
              <p>{refreshingResult ? 'Syncing result and wallet...' : displayMessage}</p>
            </div>
            <div className="portrait-dock-actions">
              <button className={`primary-btn portrait-roll-btn ${online.isOnlineMatch && online.isTransitionPhase ? 'loading-btn' : ''}`} onClick={rollDice} onTouchStart={() => ensureAudioUnlocked()} type="button" disabled={actionButtonDisabled}>{actionButtonLabel}</button>
              <button className="secondary-btn portrait-reset-btn" onClick={resetGame} type="button">{online.isOnlineMatch ? 'Leave' : 'Reset'}</button>
            </div>
          </div>

          <div className="portrait-bottom-info">
            <span className={`board-status-pill ${online.isOnlineMatch && online.isMyTurn ? 'active' : ''}`}>{online.isOnlineMatch ? (online.isMyTurn ? 'Your move' : 'Opponent move') : 'Practice'}</span>
            <span className={`board-status-pill phase-${displayPhase}`}>{phaseLabel}</span>
            <span className="board-status-pill">Wallet {walletBalance}</span>
          </div>
        </div>
        </div>
      </div>
      </div>
    </motion.div>
  );
}
